/**
 * AccountService — Account-Endpunkte über die http-Instanz (Spec §7.3).
 * Die http-Instanz entpackt `response.data` bereits im Interceptor, daher
 * geben die Methoden die Nutzdaten direkt zurück.
 */

import http from './http'
import type { Account } from '../types'

/** Backend liefert Mongo-`lean()`-Dokumente mit `_id` (kein `id`-Virtual). */
type RawAccount = Omit<Account, 'id'> & { id?: string; _id?: string }

/** Normalisiert `_id` → `id`, damit der Account-Kontrakt (`id`) überall stimmt. */
function withId(a: RawAccount): Account {
  return { ...a, id: a.id ?? a._id ?? '' } as Account
}

/** Payload zum Anlegen eines Admin-/Brand-Admin-Accounts (Spec §7.3, Super-Admin only).
 *  Ohne `password`: Invite-first — passwortloses Konto + Einladungs-Mail (7-Tage-Link). */
export interface CreateAccountPayload {
  email: string
  password?: string
  role: 'admin' | 'brand-admin'
  brandKey?: string
  displayName?: string
}

/**
 * Listet Accounts, optional gefiltert nach Brand.
 *
 * @param brandKey Optionaler Brand-Filter (Query-Param)
 * @returns Liste der (für die Rolle sichtbaren) Accounts
 */
async function list(brandKey?: string): Promise<Account[]> {
  const params = brandKey ? { brandKey } : {}
  const rows: RawAccount[] = await http.get('/accounts', { params })
  return rows.map(withId)
}

/**
 * Holt einen einzelnen Account über seine ID.
 *
 * @param id ID des Accounts
 * @returns Der Account
 */
async function get(id: string): Promise<Account> {
  return withId(await http.get(`/accounts/${id}`))
}

/**
 * Legt einen neuen Admin-/Brand-Admin-Account an (nur Super-Admin, Backend-Guard).
 * 409 bei bereits vergebener E-Mail.
 *
 * @param payload Zugangsdaten + Rolle (+ Brand bei brand-admin)
 * @returns Der angelegte Account (ohne passwordHash)
 */
async function create(payload: CreateAccountPayload): Promise<Account> {
  return withId(await http.post('/accounts', payload))
}

/**
 * Aktualisiert einen Account (Teil-Update).
 *
 * @param id ID des zu ändernden Accounts
 * @param payload Teilweiser Editier-Payload
 * @returns Der aktualisierte Account
 */
async function update(id: string, payload: Partial<Account>): Promise<Account> {
  return withId(await http.patch(`/accounts/${id}`, payload))
}

/**
 * Löscht einen Account.
 *
 * @param id ID des zu löschenden Accounts
 */
async function remove(id: string): Promise<void> {
  await http.delete(`/accounts/${id}`)
}

/**
 * Sendet die Einladungs-Mail (frischer 7-Tage-Link) an ein Admin-Konto erneut.
 *
 * @param id ID des Admin-/Brand-Admin-Accounts
 */
async function resendInvite(id: string): Promise<void> {
  await http.post(`/accounts/${id}/invite`, {})
}

export const AccountService = {
  list,
  get,
  create,
  update,
  remove,
  resendInvite,
}
